import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlbumExportButton } from "@wedding/album-export";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Heart, Plus, Trash2, Pencil, Check, X, Loader2, MessageCircle,
  ChevronLeft, ChevronRight, Play,
  Globe, Lock, ImageIcon, FolderOpen, Search,
  Download, Eye, EyeOff, ArrowLeft,
} from "lucide-react";
import {
  createAdminAlbumApi,
  createAdminMediaSocialApi,
  type AlbumSummary,
  type AlbumItem,
  type GalleryMedia,
  type MediaComment,
} from "../lib/api";
import { cn } from "../lib/utils";

// ─── Shared types ─────────────────────────────────────────────

interface LightboxMedia {
  id: string;
  kind: "image" | "video";
  r2_key: string;
  created_at: string;
  guest_name?: string | null;
  guest_phone?: string | null;
  like_count: number;
  comment_count: number;
  album_ids: string[];
}

function albumItemToLightbox(item: AlbumItem, albumId?: string): LightboxMedia {
  return {
    id: item.media_id,
    kind: item.kind,
    r2_key: item.r2_key,
    created_at: item.created_at,
    guest_name: null,
    guest_phone: null,
    like_count: 0,
    comment_count: 0,
    album_ids: albumId ? [albumId] : [],
  };
}

// ─── GalleryPage ──────────────────────────────────────────────

export function GalleryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const api = useMemo(() => createAdminAlbumApi(slug!), [slug]);
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<"gallery" | "albums">("gallery");
  const [albumDetailId, setAlbumDetailId] = useState<string | null>(null);
  const [albumModal, setAlbumModal] = useState<{
    mode: "create" | "edit";
    album?: AlbumSummary;
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [galleryMedia, setGalleryMedia] = useState<GalleryMedia[]>([]);
  const [galleryHasMore, setGalleryHasMore] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const galleryLoadingRef = useRef(false);
  const galleryCursorRef = useRef<string | undefined>(undefined);
  const activeFiltersRef = useRef<{ kind?: "image" | "video"; search?: string }>({});
  const [filterType, setFilterType] = useState<"all" | "image" | "video">("all");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [albumDetailItems, setAlbumDetailItems] = useState<AlbumItem[]>([]);
  const [albumDetailHasMore, setAlbumDetailHasMore] = useState(false);
  const [albumDetailLoading, setAlbumDetailLoading] = useState(false);
  const albumDetailLoadingRef = useRef(false);
  const albumDetailCursorRef = useRef<string | undefined>(undefined);
  const [albumSortOrder, setAlbumSortOrder] = useState<"desc" | "asc">("desc");
  const albumSortRef = useRef<"desc" | "asc">("desc");

  const [lightboxItems, setLightboxItems] = useState<LightboxMedia[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxExcludeAlbumId, setLightboxExcludeAlbumId] = useState<string | null>(null);

  const [quickAddMediaId, setQuickAddMediaId] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const { data: albumsRes } = useQuery({
    queryKey: ["admin", slug, "albums"],
    queryFn: () => api.listAlbums(),
  });
  const albums = albumsRes?.data ?? [];
  const favAlbum = albums.find(a => a.kind === "favorites");

  const fetchGallery = useCallback(async (reset = false) => {
    if (galleryLoadingRef.current) return;
    galleryLoadingRef.current = true;
    setGalleryLoading(true);
    try {
      const cursor = reset ? undefined : galleryCursorRef.current;
      const res = await api.getGallery(cursor, activeFiltersRef.current);
      const items = res.data?.media ?? [];
      setGalleryMedia(prev => (reset ? items : [...prev, ...items]));
      setGalleryHasMore(res.data?.hasMore ?? false);
      const newCursor = items.length > 0 ? items[items.length - 1].created_at : undefined;
      galleryCursorRef.current = newCursor;
    } finally {
      galleryLoadingRef.current = false;
      setGalleryLoading(false);
    }
  }, [api]);

  const fetchAlbumItems = useCallback(async (albumId: string, reset = false) => {
    if (albumDetailLoadingRef.current) return;
    albumDetailLoadingRef.current = true;
    setAlbumDetailLoading(true);
    try {
      const cursor = reset ? undefined : albumDetailCursorRef.current;
      const res = await api.getAlbumItems(albumId, cursor, albumSortRef.current);
      const items = res.data ?? [];
      setAlbumDetailItems(prev => (reset ? items : [...prev, ...items]));
      setAlbumDetailHasMore(res.hasMore ?? false);
      albumDetailCursorRef.current = items.length > 0 ? items[items.length - 1].added_at : undefined;
    } finally {
      albumDetailLoadingRef.current = false;
      setAlbumDetailLoading(false);
    }
  }, [api]);

  useEffect(() => {
    activeFiltersRef.current = {
      kind: filterType !== "all" ? filterType : undefined,
      search: searchQuery || undefined,
    };
    setGalleryMedia([]);
    galleryCursorRef.current = undefined;
    fetchGallery(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, searchQuery]);

  useEffect(() => {
    if (!albumDetailId) return;
    setAlbumDetailItems([]);
    albumDetailCursorRef.current = undefined;
    fetchAlbumItems(albumDetailId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumDetailId]);

  useEffect(() => {
    if (!quickAddMediaId) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-quick-add]")) setQuickAddMediaId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [quickAddMediaId]);

  const favoriteMut = useMutation({
    mutationFn: (mediaId: string) => api.toggleFavorite(mediaId),
    onSuccess: (res, mediaId) => {
      const favorited = res.data?.favorited ?? false;
      setGalleryMedia(prev =>
        prev.map(m =>
          m.id === mediaId
            ? {
                ...m,
                is_favorite: favorited,
                album_ids: favorited
                  ? [...m.album_ids, favAlbum?.id ?? ""]
                  : m.album_ids.filter(id => id !== favAlbum?.id),
              }
            : m,
        ),
      );
      qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
      showToast(favorited ? t("gallery.addedToFavorites") : t("gallery.removedFromFavorites"));
    },
  });

  const addSingleMut = useMutation({
    mutationFn: ({ albumId, mediaId }: { albumId: string; mediaId: string }) =>
      api.addItem(albumId, mediaId),
    onSuccess: (_res, { albumId, mediaId }) => {
      qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
      setQuickAddMediaId(null);
      const album = albums.find(a => a.id === albumId);
      showToast(
        album?.kind === "favorites" ? t("gallery.addedToFavorites") : t("gallery.addedToAlbum"),
      );
      setGalleryMedia(prev =>
        prev.map(m =>
          m.id === mediaId && !m.album_ids.includes(albumId)
            ? { ...m, album_ids: [...m.album_ids, albumId] }
            : m,
        ),
      );
      setLightboxItems(prev =>
        prev?.map(m =>
          m.id === mediaId && !m.album_ids.includes(albumId)
            ? { ...m, album_ids: [...m.album_ids, albumId] }
            : m,
        ) ?? null,
      );
    },
  });

  const removeSingleMut = useMutation({
    mutationFn: ({ albumId, mediaId }: { albumId: string; mediaId: string }) =>
      api.removeItem(albumId, mediaId),
    onSuccess: (_res, { albumId, mediaId }) => {
      qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
      setQuickAddMediaId(null);
      const album = albums.find(a => a.id === albumId);
      showToast(
        album?.kind === "favorites" ? t("gallery.removedFromFavorites") : t("gallery.removedFromAlbum"),
      );
      setGalleryMedia(prev =>
        prev.map(m =>
          m.id === mediaId
            ? {
                ...m,
                album_ids: m.album_ids.filter(id => id !== albumId),
                is_favorite: album?.kind === "favorites" ? false : m.is_favorite,
              }
            : m,
        ),
      );
      setLightboxItems(prev =>
        prev?.map(m =>
          m.id === mediaId ? { ...m, album_ids: m.album_ids.filter(id => id !== albumId) } : m,
        ) ?? null,
      );
    },
  });

  function openGalleryLightbox(idx: number) {
    setLightboxItems(
      galleryMedia.map(m => ({
        id: m.id,
        kind: m.kind,
        r2_key: m.r2_key,
        created_at: m.created_at,
        guest_name: m.guest_name,
        guest_phone: m.guest_phone,
        like_count: m.like_count,
        comment_count: m.comment_count,
        album_ids: m.album_ids,
      })),
    );
    setLightboxIndex(idx);
    setLightboxExcludeAlbumId(null);
  }

  function openAlbumLightbox(idx: number) {
    setLightboxItems(
      albumDetailItems.map(item => albumItemToLightbox(item, albumDetailId ?? undefined)),
    );
    setLightboxIndex(idx);
    setLightboxExcludeAlbumId(albumDetailId);
  }

  const currentAlbum = albums.find(a => a.id === albumDetailId);

  return (
    <div className="flex flex-col min-h-0 h-full">
      {lightboxItems && (
        <Lightbox
          items={lightboxItems}
          index={lightboxIndex}
          onClose={() => setLightboxItems(null)}
          onNavigate={setLightboxIndex}
          albums={albums}
          excludeAlbumId={lightboxExcludeAlbumId}
          onToggleAlbum={(albumId, inAlbum) => {
            const mediaId = lightboxItems[lightboxIndex]?.id;
            if (!mediaId) return;
            inAlbum
              ? removeSingleMut.mutate({ albumId, mediaId })
              : addSingleMut.mutate({ albumId, mediaId });
          }}
          onCommentCountChange={(mediaId, delta) =>
            setGalleryMedia(prev =>
              prev.map(m =>
                m.id === mediaId ? { ...m, comment_count: Math.max(0, m.comment_count + delta) } : m,
              ),
            )
          }
          slug={slug!}
          showToast={showToast}
        />
      )}

      {albumModal && (
        <AlbumModal
          mode={albumModal.mode}
          album={albumModal.album}
          api={api}
          onClose={() => setAlbumModal(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
            setAlbumModal(null);
          }}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
            setAlbumModal(null);
            if (albumDetailId === albumModal.album?.id) setAlbumDetailId(null);
          }}
          showToast={showToast}
        />
      )}

      {/* Tab nav */}
      <div className="border-b border-border px-4 md:px-6 shrink-0">
        <nav className="flex">
          {(["gallery", "albums"] as const).map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setAlbumDetailId(null);
              }}
              className={cn(
                "px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab === "gallery" ? t("gallery.title") : t("gallery.manageAlbums")}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* ── Gallery Tab ── */}
        {activeTab === "gallery" && (
          <>
            {/* Albümün tamamı (yalnız onaylı) — ortak paket, organizatör ⇄ admin aynı */}
            <div className="mb-4 flex justify-end">
              <AlbumExportButton apiBase={`/api/admin/w/${slug}`} onNotify={showToast} />
            </div>

            <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                {(["all", "image", "video"] as const).map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={cn(
                      "rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap",
                      filterType === type
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground",
                    )}
                  >
                    {type === "all"
                      ? t("gallery.filterAll")
                      : type === "image"
                        ? t("gallery.filterPhotos")
                        : t("gallery.filterVideos")}
                  </button>
                ))}
              </div>
              <div className="relative sm:ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={searchDraft}
                  onChange={e => {
                    setSearchDraft(e.target.value);
                    if (e.target.value === "") setSearchQuery("");
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") setSearchQuery(searchDraft);
                    if (e.key === "Escape") { setSearchDraft(""); setSearchQuery(""); }
                  }}
                  placeholder={t("gallery.searchByName")}
                  className="w-full sm:w-56 rounded-full border border-border bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
              </div>
            </div>

            {galleryLoading && galleryMedia.length === 0 ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
              </div>
            ) : galleryMedia.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <ImageIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                <p className="text-muted-foreground">{t("gallery.noMedia")}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                  {galleryMedia.map((m, idx) => (
                    <MediaCard
                      key={m.id}
                      item={m}
                      quickAddOpen={quickAddMediaId === m.id}
                      albums={albums}
                      onToggleFavorite={() => favoriteMut.mutate(m.id)}
                      onOpenLightbox={() => openGalleryLightbox(idx)}
                      onToggleQuickAdd={() =>
                        setQuickAddMediaId(prev => (prev === m.id ? null : m.id))
                      }
                      onQuickAddToAlbum={albumId =>
                        addSingleMut.mutate({ albumId, mediaId: m.id })
                      }
                      onQuickRemoveFromAlbum={albumId =>
                        removeSingleMut.mutate({ albumId, mediaId: m.id })
                      }
                    />
                  ))}
                </div>
                {galleryHasMore && (
                  <div className="mt-6 flex justify-center">
                    <button
                      onClick={() => fetchGallery(false)}
                      disabled={galleryLoading}
                      className="flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      {galleryLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t("gallery.loadMore")}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Albums Tab ── */}
        {activeTab === "albums" && (
          albumDetailId ? (
            <>
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <button
                    onClick={() => setAlbumDetailId(null)}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("gallery.backToAlbums")}
                  </button>
                  <h2 className="mt-2 text-xl font-semibold text-foreground">
                    {currentAlbum?.name ?? ""}
                  </h2>
                </div>
                <div className="flex shrink-0 items-center gap-2 mt-6">
                  <button
                    onClick={() => {
                      const next = albumSortOrder === "desc" ? "asc" : "desc";
                      setAlbumSortOrder(next);
                      albumSortRef.current = next;
                      albumDetailCursorRef.current = undefined;
                      fetchAlbumItems(albumDetailId!, true);
                    }}
                    className="flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {albumSortOrder === "desc" ? t("gallery.filterNewest") : t("gallery.filterOldest")}
                  </button>
                  <button
                    onClick={() => setPickerOpen(true)}
                    className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    <Plus className="h-4 w-4" />
                    {t("gallery.addFromGallery")}
                  </button>
                </div>
              </div>

              {pickerOpen && albumDetailId && (
                <MediaPickerModal
                  albumId={albumDetailId}
                  existingIds={new Set(albumDetailItems.map(i => i.media_id))}
                  api={api}
                  onClose={() => setPickerOpen(false)}
                  onAdded={(newItems) => {
                    setAlbumDetailItems(prev => [...prev, ...newItems]);
                    qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
                    setPickerOpen(false);
                    showToast(t("gallery.addedToAlbum"));
                  }}
                />
              )}

              {albumDetailLoading && albumDetailItems.length === 0 ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
                </div>
              ) : albumDetailItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <FolderOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                  <p className="text-muted-foreground">{t("gallery.noMedia")}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                    {albumDetailItems.map((item, idx) => (
                      <AlbumItemCard
                        key={item.media_id}
                        item={item}
                        onOpenLightbox={() => openAlbumLightbox(idx)}
                        onRemove={async () => {
                          if (!albumDetailId) return;
                          try {
                            await api.removeItem(albumDetailId, item.media_id);
                            setAlbumDetailItems(prev => prev.filter(i => i.media_id !== item.media_id));
                            if (currentAlbum?.cover_media_id === item.media_id) {
                              await api.setCover(albumDetailId, null);
                            }
                            qc.invalidateQueries({ queryKey: ["admin", slug, "albums"] });
                            showToast(t("gallery.removedFromAlbum"));
                          } catch {
                            showToast(t("common.error"), false);
                          }
                        }}
                      />
                    ))}
                  </div>
                  {albumDetailHasMore && (
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={() => fetchAlbumItems(albumDetailId, false)}
                        disabled={albumDetailLoading}
                        className="flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
                      >
                        {albumDetailLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t("gallery.loadMore")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground">
                  {t("gallery.manageAlbums")}
                </h2>
                <button
                  onClick={() => setAlbumModal({ mode: "create" })}
                  className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  {t("gallery.newAlbum")}
                </button>
              </div>

              {albums.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <FolderOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                  <p className="text-muted-foreground">{t("gallery.noAlbums")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {albums.map(album => (
                    <AlbumCard
                      key={album.id}
                      album={album}
                      onOpen={() => setAlbumDetailId(album.id)}
                      onEdit={() => setAlbumModal({ mode: "edit", album })}
                    />
                  ))}
                </div>
              )}
            </>
          )
        )}
      </div>

      {toast && (
        <div
          className={cn(
            "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-2xl px-4 py-2.5 text-sm font-medium text-white shadow-lg pointer-events-none",
            toast.ok ? "bg-emerald-600" : "bg-red-600",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── MediaCard ────────────────────────────────────────────────

function MediaCard({
  item, quickAddOpen, albums, onToggleFavorite, onOpenLightbox,
  onToggleQuickAdd, onQuickAddToAlbum, onQuickRemoveFromAlbum,
}: {
  item: GalleryMedia;
  quickAddOpen: boolean;
  albums: AlbumSummary[];
  onToggleFavorite: () => void;
  onOpenLightbox: () => void;
  onToggleQuickAdd: () => void;
  onQuickAddToAlbum: (albumId: string) => void;
  onQuickRemoveFromAlbum: (albumId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="group relative aspect-square overflow-hidden rounded-2xl cursor-pointer transition-all bg-muted hover:ring-1 hover:ring-primary/40 hover:ring-offset-1 hover:ring-offset-background"
      onClick={onOpenLightbox}
    >
      {item.kind === "image" ? (
        <img src={`/cdn/${item.r2_key}`} className="h-full w-full object-cover" loading="lazy" alt="" />
      ) : (
        <>
          <video src={`/cdn/${item.r2_key}`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-full bg-black/50 p-2.5">
              <Play className="h-5 w-5 text-white fill-white" />
            </div>
          </div>
        </>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

      <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
        <button
          onClick={e => { e.stopPropagation(); onToggleFavorite(); }}
          className={cn(
            "rounded-full p-1.5 backdrop-blur-sm transition-all cursor-pointer",
            item.is_favorite ? "bg-black/40 opacity-100" : "bg-black/40 opacity-0 group-hover:opacity-100",
          )}
        >
          <Heart className={cn("h-3.5 w-3.5", item.is_favorite ? "fill-rose-400 text-rose-400" : "text-white")} />
        </button>

        <div data-quick-add className="relative">
          <button
            onClick={e => { e.stopPropagation(); onToggleQuickAdd(); }}
            className="rounded-full p-1.5 bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5 text-white" />
          </button>
          {quickAddOpen && (
            <div
              data-quick-add
              className="absolute right-0 top-full mt-1 z-20 min-w-[150px] rounded-2xl border border-border bg-card shadow-lg py-1"
              onClick={e => e.stopPropagation()}
            >
              {albums.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">{t("gallery.noAlbums")}</p>
              ) : (
                albums.map(album => {
                  const inAlbum = item.album_ids.includes(album.id);
                  return (
                    <button
                      key={album.id}
                      onClick={() => inAlbum ? onQuickRemoveFromAlbum(album.id) : onQuickAddToAlbum(album.id)}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors cursor-pointer"
                    >
                      {inAlbum ? (
                        <Check className="h-3 w-3 text-primary" />
                      ) : album.kind === "favorites" ? (
                        <Heart className="h-3 w-3 text-rose-500" />
                      ) : (
                        <Plus className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="flex-1 truncate">{album.name}</span>
                      {inAlbum && <span className="text-[10px] text-primary font-semibold">✓</span>}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AlbumCard ────────────────────────────────────────────────

function AlbumCard({ album, onOpen, onEdit }: { album: AlbumSummary; onOpen: () => void; onEdit: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="group relative rounded-2xl overflow-hidden bg-card border border-border cursor-pointer" onClick={onOpen}>
      <div className="aspect-square relative overflow-hidden bg-primary/5">
        {album.cover_r2_key ? (
          <img src={`/cdn/${album.cover_r2_key}`} alt={album.name} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300" />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <FolderOpen className="h-10 w-10 text-primary/30" />
          </div>
        )}
        {album.cover_r2_key && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        )}
      </div>
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          {album.is_public ? (
            <Globe className="h-3.5 w-3.5 text-primary shrink-0" />
          ) : (
            <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <p className="text-sm font-medium text-foreground truncate flex-1">{album.name}</p>
          <button
            onClick={e => { e.stopPropagation(); onEdit(); }}
            className="shrink-0 size-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("gallery.itemCount", { count: album.item_count })}
        </p>
      </div>
    </div>
  );
}

// ─── AlbumItemCard ────────────────────────────────────────────

function AlbumItemCard({ item, onOpenLightbox, onRemove }: { item: AlbumItem; onOpenLightbox: () => void; onRemove?: () => void }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      className="group relative aspect-square overflow-hidden rounded-2xl cursor-pointer bg-muted hover:ring-1 hover:ring-primary/40 hover:ring-offset-1 hover:ring-offset-background transition-all"
      onClick={onOpenLightbox}
    >
      {item.kind === "image" ? (
        <img src={`/cdn/${item.r2_key}`} className="h-full w-full object-cover" loading="lazy" alt="" />
      ) : (
        <>
          <video src={`/cdn/${item.r2_key}`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-full bg-black/50 p-2.5">
              <Play className="h-5 w-5 text-white fill-white" />
            </div>
          </div>
        </>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      {onRemove && !confirming && (
        <button
          onClick={e => { e.stopPropagation(); setConfirming(true); }}
          className="absolute top-2 right-2 size-7 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white hover:bg-destructive cursor-pointer z-10"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {confirming && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/70 backdrop-blur-sm rounded-2xl"
          onClick={e => e.stopPropagation()}
        >
          <p className="text-xs font-semibold text-white">{t("gallery.removeFromAlbumConfirm")}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setConfirming(false); onRemove!(); }}
              className="flex items-center justify-center size-8 rounded-full bg-destructive text-white cursor-pointer hover:bg-destructive/80 transition-colors"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="flex items-center justify-center size-8 rounded-full bg-white/20 text-white cursor-pointer hover:bg-white/30 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AlbumModal ───────────────────────────────────────────────

function AlbumModal({
  mode, album, api, onClose, onSuccess, onDeleted, showToast,
}: {
  mode: "create" | "edit";
  album?: AlbumSummary;
  api: ReturnType<typeof createAdminAlbumApi>;
  onClose: () => void;
  onSuccess: () => void;
  onDeleted: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(album?.name ?? "");
  const [isPublic, setIsPublic] = useState(Boolean(album?.is_public));
  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(album?.cover_media_id ?? null);
  const [coverItems, setCoverItems] = useState<AlbumItem[]>([]);
  const [coverHasMore, setCoverHasMore] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);
  const coverCursorRef = useRef<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchCoverItems = useCallback(async (reset = false) => {
    if (!album) return;
    setCoverLoading(true);
    try {
      const cursor = reset ? undefined : coverCursorRef.current;
      const res = await api.getAlbumItems(album.id, cursor);
      const items = res.data ?? [];
      setCoverItems(prev => reset ? items : [...prev, ...items]);
      setCoverHasMore(res.hasMore ?? false);
      coverCursorRef.current = items.length > 0 ? items[items.length - 1].created_at : undefined;
    } catch {}
    finally { setCoverLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album?.id]);

  useEffect(() => {
    if (mode === "edit" && album) fetchCoverItems(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        if (!name.trim()) return;
        await api.createAlbum(name.trim());
        showToast(t("gallery.albumCreated"));
      } else if (album) {
        const tasks: Promise<unknown>[] = [];
        if (name.trim() && name.trim() !== album.name && album.kind !== "favorites") {
          tasks.push(api.renameAlbum(album.id, name.trim()));
        }
        if (Boolean(isPublic) !== Boolean(album.is_public)) {
          tasks.push(api.setPublic(album.id, isPublic));
        }
        if (selectedCoverId !== album.cover_media_id) {
          tasks.push(api.setCover(album.id, selectedCoverId));
        }
        await Promise.all(tasks);
        showToast(t("gallery.albumSaved"));
      }
      onSuccess();
    } catch {
      showToast(t("common.error"), false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!album) return;
    setDeleting(true);
    try {
      await api.deleteAlbum(album.id);
      showToast(t("gallery.albumDeleted"));
      onDeleted();
    } catch {
      showToast(t("common.error"), false);
      setDeleting(false);
    }
  };

  const isFavorites = album?.kind === "favorites";

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">
            {mode === "create" ? t("gallery.newAlbum") : t("gallery.editAlbum")}
          </h2>
          <button onClick={onClose} className="size-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground cursor-pointer transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t("common.name")}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isFavorites}
              placeholder={t("gallery.albumName")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t("gallery.visibility")}</label>
            <select
              value={isPublic ? "public" : "private"}
              onChange={e => setIsPublic(e.target.value === "public")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 cursor-pointer"
            >
              <option value="public">{t("gallery.public")}</option>
              <option value="private">{t("gallery.private")}</option>
            </select>
          </div>

          {mode === "edit" && coverItems.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t("gallery.coverPhoto")}</label>
              <div className="grid grid-cols-4 gap-2">
                {coverItems.map(item => (
                  <button
                    key={item.media_id}
                    onClick={() => setSelectedCoverId(prev => prev === item.media_id ? null : item.media_id)}
                    className={cn(
                      "relative aspect-square rounded-2xl overflow-hidden cursor-pointer border-2 transition-all",
                      selectedCoverId === item.media_id ? "border-primary" : "border-transparent hover:border-primary/40",
                    )}
                  >
                    <img src={`/cdn/${item.r2_key}`} alt="" className="h-full w-full object-cover" />
                    {selectedCoverId === item.media_id && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <Check className="h-5 w-5 text-primary drop-shadow-md" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {coverHasMore && (
                <button
                  onClick={() => fetchCoverItems(false)}
                  disabled={coverLoading}
                  className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 py-1 cursor-pointer"
                >
                  {coverLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t("gallery.loadMore")}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border">
          <div>
            {mode === "edit" && !isFavorites && (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t("gallery.deleteAlbumConfirm")}</span>
                  <button onClick={handleDelete} disabled={deleting} className="text-xs font-semibold text-destructive hover:underline cursor-pointer disabled:opacity-50">
                    {deleting ? t("common.loading") : t("common.delete")}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} className="text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                    {t("common.cancel")}
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="text-sm text-destructive hover:text-destructive/80 transition-colors cursor-pointer flex items-center gap-1.5">
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("gallery.deleteAlbum")}
                </button>
              )
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || (mode === "create" && !name.trim())}
            className="flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── MediaPickerModal ─────────────────────────────────────────

function MediaPickerModal({
  albumId, existingIds, api, onClose, onAdded,
}: {
  albumId: string;
  existingIds: Set<string>;
  api: ReturnType<typeof createAdminAlbumApi>;
  onClose: () => void;
  onAdded: (items: AlbumItem[]) => void;
}) {
  const { t } = useTranslation();
  const [media, setMedia] = useState<GalleryMedia[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const cursorRef = useRef<string | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const activeSearchRef = useRef("");

  const fetchMedia = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const cursor = reset ? undefined : cursorRef.current;
      const res = await api.getGallery(cursor, activeSearchRef.current ? { search: activeSearchRef.current } : undefined);
      const items = res.data?.media ?? [];
      setMedia(prev => (reset ? items : [...prev, ...items]));
      setHasMore(res.data?.hasMore ?? false);
      cursorRef.current = items.length > 0 ? items[items.length - 1].created_at : undefined;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    activeSearchRef.current = searchQuery;
    cursorRef.current = undefined;
    fetchMedia(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery) fetchMedia(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function toggleSelect(id: string) {
    if (existingIds.has(id)) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleAdd() {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const ids = [...selected];
      await Promise.all(ids.map(id => api.addItem(albumId, id)));
      const addedItems: AlbumItem[] = ids.flatMap(id => {
        const m = media.find(x => x.id === id);
        if (!m) return [];
        return [{
          media_id: m.id,
          r2_key: m.r2_key,
          kind: m.kind,
          content_type: m.content_type,
          created_at: m.created_at,
          position: 0,
          added_at: new Date().toISOString(),
        }];
      });
      onAdded(addedItems);
    } catch {}
    finally { setAdding(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[88vh]" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">{t("gallery.addFromGallery")}</h2>
            <button onClick={onClose} className="size-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground cursor-pointer transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchDraft}
              onChange={e => { setSearchDraft(e.target.value); if (e.target.value === "") setSearchQuery(""); }}
              onKeyDown={e => {
                if (e.key === "Enter") setSearchQuery(searchDraft);
                if (e.key === "Escape") { setSearchDraft(""); setSearchQuery(""); }
              }}
              placeholder={t("gallery.searchGuest")}
              className="w-full rounded-full border border-border bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading && media.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-primary/40" />
            </div>
          ) : media.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ImageIcon className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t("gallery.noMedia")}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {media.map(m => {
                  const already = existingIds.has(m.id);
                  const isSelected = selected.has(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => toggleSelect(m.id)}
                      disabled={already}
                      className={cn(
                        "relative aspect-square rounded-2xl overflow-hidden cursor-pointer transition-all",
                        already ? "opacity-40 cursor-not-allowed"
                          : isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                          : "hover:ring-1 hover:ring-primary/40 hover:ring-offset-1 hover:ring-offset-background",
                      )}
                    >
                      {m.kind === "image" ? (
                        <img src={`/cdn/${m.r2_key}`} className="h-full w-full object-cover" loading="lazy" alt="" />
                      ) : (
                        <>
                          <video src={`/cdn/${m.r2_key}`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="rounded-full bg-black/50 p-2">
                              <Play className="h-4 w-4 text-white fill-white" />
                            </div>
                          </div>
                        </>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="size-6 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3.5 w-3.5 text-white" />
                          </div>
                        </div>
                      )}
                      {already && (
                        <div className="absolute inset-0 flex items-end justify-center pb-1.5">
                          <span className="text-[10px] font-medium text-white bg-black/60 rounded-full px-2 py-0.5">
                            {t("gallery.alreadyInAlbum")}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => fetchMedia(false)}
                    disabled={loading}
                    className="flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {t("gallery.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border shrink-0">
          <span className="text-sm text-muted-foreground">
            {selected.size > 0 ? t("gallery.selectedCount", { count: selected.size }) : ""}
          </span>
          <button
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
            className="flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50"
          >
            {adding && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("gallery.addSelected", { count: selected.size })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Lightbox ────────────────────────────────────────────────

function Lightbox({
  items, index, onClose, onNavigate, albums, excludeAlbumId,
  onToggleAlbum, onCommentCountChange, slug, showToast,
}: {
  items: LightboxMedia[];
  index: number;
  onClose: () => void;
  onNavigate: (i: number) => void;
  albums: AlbumSummary[];
  excludeAlbumId?: string | null;
  onToggleAlbum: (albumId: string, inAlbum: boolean) => void;
  onCommentCountChange: (mediaId: string, delta: number) => void;
  slug: string;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;
  const visibleAlbums = excludeAlbumId ? albums.filter(a => a.id !== excludeAlbumId) : albums;

  const socialApi = useMemo(() => createAdminMediaSocialApi(slug), [slug]);
  const [comments, setComments] = useState<MediaComment[]>([]);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [commentsCursor, setCommentsCursor] = useState<string | undefined>();
  const [likeCount, setLikeCount] = useState(item?.like_count ?? 0);
  const [commentCount, setCommentCount] = useState(item?.comment_count ?? 0);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const commentsLoadingRef = useRef(false);
  const [albumMenuOpen, setAlbumMenuOpen] = useState(false);

  const fetchComments = useCallback(async (mediaId: string, reset = false) => {
    if (commentsLoadingRef.current) return;
    commentsLoadingRef.current = true;
    setCommentsLoading(true);
    try {
      const cursor = reset ? undefined : commentsCursor;
      const res = await socialApi.getComments(mediaId, cursor);
      setComments(prev => reset ? res.comments : [...prev, ...res.comments]);
      setCommentsHasMore(res.hasMore);
      setCommentsCursor(res.comments.length > 0 ? res.comments[res.comments.length - 1].created_at : undefined);
      if (reset) {
        setLikeCount(res.like_count);
        setCommentCount(res.comment_count);
      }
    } catch {}
    finally {
      commentsLoadingRef.current = false;
      setCommentsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialApi, commentsCursor]);

  useEffect(() => {
    if (!item) return;
    setComments([]);
    setCommentsHasMore(false);
    setCommentsCursor(undefined);
    commentsLoadingRef.current = false;
    setLikeCount(item.like_count ?? 0);
    setCommentCount(item.comment_count ?? 0);
    fetchComments(item.id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(index - 1);
      if (e.key === "ArrowRight" && hasNext) onNavigate(index + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, hasPrev, hasNext, onClose, onNavigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const [swipeHint, setSwipeHint] = useState(items.length > 1);
  useEffect(() => {
    if (!swipeHint) return;
    const timer = setTimeout(() => setSwipeHint(false), 2000);
    return () => clearTimeout(timer);
  }, [swipeHint]);

  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx) && dy > 0) { onClose(); return; }
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0 && hasNext) onNavigate(index + 1);
      if (dx > 0 && hasPrev) onNavigate(index - 1);
    }
    setTouchStart(null);
  }, [touchStart, index, hasPrev, hasNext, onClose, onNavigate]);

  const handleDeleteComment = async (commentId: string) => {
    try {
      await socialApi.deleteComment(item.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCommentCount(n => Math.max(0, n - 1));
      onCommentCountChange(item.id, -1);
      showToast(t("gallery.commentDeleted"));
    } catch {
      showToast(t("common.error"), false);
    }
  };

  const handleToggleHide = async (comment: MediaComment) => {
    try {
      const newHidden = !comment.is_hidden;
      await socialApi.toggleCommentVisibility(item.id, comment.id, newHidden);
      setComments(prev => prev.map(c => (c.id === comment.id ? { ...c, is_hidden: newHidden } : c)));
      showToast(newHidden ? t("gallery.commentHiddenSuccess") : t("gallery.commentUnhiddenSuccess"));
    } catch {
      showToast(t("common.error"), false);
    }
  };

  if (!item) return null;

  const uploaderName = item.guest_name ?? item.guest_phone ?? t("gallery.guest");
  const uploaderInitial = uploaderName[0]?.toUpperCase() ?? "?";
  const formattedDate = new Date(item.created_at).toLocaleString(i18n.language, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const mediaAlbumIds = item.album_ids ?? [];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 lg:p-12"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[10000] size-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label={t("gallery.close")}
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex flex-col lg:flex-row w-full h-full max-w-7xl rounded-2xl overflow-hidden shadow-2xl">
        {/* ── Left: Media (70%) ── */}
        <div className="relative flex flex-1 lg:w-[70%] bg-neutral-950 items-center justify-center overflow-hidden">
          {hasPrev && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index - 1); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index + 1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}

          {item.kind === "image" ? (
            <img key={item.id} src={`/cdn/${item.r2_key}`} alt="" className="max-h-full max-w-full object-contain select-none" draggable={false} />
          ) : (
            <video key={item.id} src={`/cdn/${item.r2_key}`} className="max-h-full max-w-full object-contain" controls autoPlay playsInline />
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-white/60 bg-black/40 px-3 py-1 rounded-full pointer-events-none">
            {index + 1} / {items.length}
          </div>

          {swipeHint && (
            <div className="absolute inset-x-0 bottom-14 flex justify-center pointer-events-none md:hidden animate-fade-out">
              <div className="flex items-center gap-3 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 text-white/80 text-xs font-medium">
                <ChevronLeft className="h-4 w-4" />
                <span>{t("gallery.swipeHint")}</span>
                <ChevronRight className="h-4 w-4" />
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Info Panel (30%) ── */}
        <div className="flex flex-col lg:w-[30%] lg:min-w-[300px] border-t border-border lg:border-t-0 lg:border-l border-border max-h-[45vh] lg:max-h-full bg-card">
          <div className="flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b border-border">
            <div className="size-10 rounded-full bg-primary/20 border border-border flex items-center justify-center text-sm font-bold text-primary shrink-0 select-none">
              {uploaderInitial}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground truncate">{uploaderName}</p>
              <p className="text-xs text-muted-foreground">{formattedDate}</p>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground text-sm shrink-0">
              <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{likeCount}</span>
              <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{commentCount}</span>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
            {commentsLoading && comments.length === 0 ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground/60 py-6">{t("gallery.noComments")}</p>
            ) : (
              <>
                {commentsHasMore && (
                  <button
                    onClick={() => fetchComments(item.id, false)}
                    disabled={commentsLoading}
                    className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 py-1 cursor-pointer"
                  >
                    {commentsLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                    {t("gallery.loadMore")}
                  </button>
                )}
                {comments.map(comment => (
                  <div key={comment.id} className={cn("flex gap-3 group", !!comment.is_hidden && "opacity-50")}>
                    <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0 select-none">
                      {comment.author_name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground leading-snug">{comment.author_name}</p>
                      <p className={cn("text-sm text-muted-foreground break-words leading-snug mt-0.5", !!comment.is_hidden && "line-through")}>
                        {comment.content}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground/50">
                          {new Date(comment.created_at).toLocaleString(i18n.language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {!!comment.is_hidden && (
                          <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">{t("gallery.commentHidden")}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-1 shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleHide(comment)}
                        className="text-muted-foreground/50 hover:text-amber-500 transition-colors cursor-pointer"
                        title={comment.is_hidden ? t("gallery.unhideComment") : t("gallery.hideComment")}
                      >
                        {comment.is_hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-muted-foreground/50 hover:text-destructive transition-colors cursor-pointer"
                        title={t("gallery.deleteComment")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="flex-shrink-0 border-t border-border px-5 py-3 flex items-center justify-between gap-3">
            <a
              href={`/cdn/${item.r2_key}`}
              download
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer text-sm"
            >
              <Download className="h-4 w-4" />
              {t("gallery.download")}
            </a>

            <div className="relative">
              <button
                onClick={() => setAlbumMenuOpen(v => !v)}
                className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                {t("gallery.addToAlbum")}
              </button>
              {albumMenuOpen && (
                <div className="absolute bottom-full mb-2 right-0 min-w-[160px] rounded-2xl border border-border bg-card shadow-lg py-1 z-10">
                  {visibleAlbums.map(album => {
                    const inAlbum = mediaAlbumIds.includes(album.id);
                    return (
                      <button
                        key={album.id}
                        onClick={() => { onToggleAlbum(album.id, inAlbum); setAlbumMenuOpen(false); }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors cursor-pointer"
                      >
                        {inAlbum ? <Check className="h-3 w-3 text-primary" />
                          : album.kind === "favorites" ? <Heart className="h-3 w-3 text-rose-500" />
                          : <Plus className="h-3 w-3 text-muted-foreground" />}
                        <span className="flex-1 truncate">{album.name}</span>
                        {inAlbum && <span className="text-xs text-primary">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
